import React from 'react';
import { X } from 'lucide-react';

const PrivacyModal = ({ isOpen, onClose }) => {
    React.useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="privacy-modal-title"
        >
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 z-10"
                    aria-label="Close modal"
                >
                    <X className="w-6 h-6 text-gray-500 hover:text-gray-700" />
                </button>

                <div className="p-6 border-b border-gray-200">
                    <h2 id="privacy-modal-title" className="text-2xl font-bold text-gray-800 pr-12">Privacy Policy</h2>
                </div>

                <div className="p-6 space-y-6 text-gray-700">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">A1 Moving Solutions ("we", "our", or "us") respects your privacy and is committed to protecting
                            the personal information you share with us. This Privacy Policy outlines how we collect, use,
                            and protect your information, especially when you interact with us via SMS/text messages.</h3>
                        <div className="text-sm text-gray-500 border-t pt-4">
                            <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>
                        </div>
                        <br></br>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Information We Collect</h3>
                        <p className="mb-3">
                            We may collect the following types of personal information when you visit our website, request a
                            quote, or communicate with us via SMS:
                        </p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>Contact Information: Name, phone number, email address</li>
                            <li>Moving Information: Address details (pickup and destination), preferred moving dates,
                                size of move</li>
                            <li>Communication Data: Text message content, time stamps, and responses</li>

                        </ul>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">How We Use Your Information</h3>
                        <p className="mb-3">We use the information we collect to:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>Respond to your inquiries and provide moving quotes</li>
                            <li>Communicate with you via SMS and email</li>
                            <li>Schedule and coordinate moving services</li>
                            <li>Improve our services and customer experience</li>
                            <li>Maintain internal records for operational purposes</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Who We Share Your Information With</h3>
                        <p className="mb-3">
                            We <b>do not sell, rent, or share your personal information</b> with third parties or affiliates for
                            marketing purposes. <br></br>
                            Specifically:
                        </p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                            <li><b>SMS consent is not shared with third parties.</b></li>
                            <li>We may share data only when legally required (e.g., to comply with a court order or law
                                enforcement request).</li>
                            <li>Trusted service providers (e.g., IT support or CRM tools) may access limited data under
                                strict confidentiality agreements and only to the extent necessary for business
                                operations.</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">SMS Communications and Consent</h3>
                        <p className="mb-3">
                            By providing your phone number, you consent to receive SMS/text messages from us related to
                            your quote or services.
                        </p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>You may opt out at any time by replying <b>STOP</b>.</li>
                            <li>Message and data rates may apply based on your mobile carrier plan.</li>
                        </ul>
                        <br></br>
                        <p className="mb-3">
                            <b>SMS Terms of Service</b>:
                            <br></br>
                            By opting into SMS from a web form or other medium, you are agreeing to receive SMS
                            messages from A1 Moving Solutions. This includes SMS messages for conversations
                            (external). Message frequency varies. Message and data rates may apply. See privacy policy
                            at https://www.a1movingsolutions.com/. Message HELP for help. Reply STOP to any message
                            to opt out.
                        </p>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">How We Protect Your Information</h3>
                        <p>
                            We use industry-standard security measures to protect your data from unauthorized access,
                            alteration, disclosure, or destruction. These include secure servers, encrypted communications,
                            and access controls.
                        </p>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Your Choices and Rights</h3>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                            <li><b>Update or correct your info</b>: Contact us to update your personal information.</li>
                            <li><b>Opt-out</b>: You can opt-out of receiving SMS or email communications at any time.</li>
                            <li><b>Delete your data</b>: You may request that we delete your personal data, subject to any
                                legal obligations we may have.</li>
                        </ul>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <h3 className="text-lg font-semibold text-red-800 mb-3">Contact Us</h3>
                        <p className="text-red-700">
                            If you have any questions or concerns about this Privacy Policy or your personal information,
                            please contact us at:
                        </p>
                        <div className="mt-2 text-red-700">
                            <p><strong>A1 Moving Solutions</strong></p>
                            <p>State Cert #: C-3118</p>
                            <p>Email: support@a1movingsolutions.com</p>
                            <p>Phone: 984-500-0425</p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PrivacyModal;
